import { Inject, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, retry } from 'rxjs';
import { map, shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';
import {
  CartItem,
  CheckoutSession,
  CLIENT_REFERENCE_ID_SEPARATOR,
  DeliveryAddress,
  DeliveryRule,
  DeliverySlot,
  DeliverySlotOpening,
  GalleryRecord,
  initShoppingCart,
  ShoppingCart,
  ShopSettings
} from 'shared';
import { AuthService } from '../services/auth.service';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { GuestShopLoginComponent } from './shop-components/guest-shop-login/guest-shop-login.component';
import { HelpService } from '../services/help.service';
import { HttpClient } from '@angular/common/http';
import { environment } from './../../environments/environment';
import { AnalyticsService } from '../services/analytics.service';
import {
  limit,
  orderBy,
  QueryConstraint,
  serverTimestamp,
  startAfter,
  where
} from '@angular/fire/firestore';
import { DbService } from './../services/db.service';

@Injectable({
  providedIn: 'root'
})
export class ShopService {
  protected _API_URL = 'https://russellsny.com/shop-api';
  private _db = inject(DbService);

  private _cart = new BehaviorSubject<ShoppingCart>({
    items: [],
    deliveryAddress: {}
  } as ShoppingCart);
  _cartSubInit = false;

  private _shopSettings = new BehaviorSubject<ShopSettings>({
    // Seeded true so ShopEnabledGuard doesn't bounce fresh deep-link loads
    // (e.g. Stripe success redirect to /shop/cart?session_id=...) to / before
    // remote config resolves — the guard uses take(1) on this BehaviorSubject.
    isShopEnabled: true,
    // Conservative seed: gate stays off until Firestore confirms it on. Prevents
    // dropship API calls (e.g. exclusivesCart.hydrate()) from firing on first paint
    // when the flag is actually off, and avoids the brief "exclusives UI flashes
    // then disappears" flicker on shell menu / cart drawer / promo banners.
    isExclusivesEnabled: false,
    isGiftCardPurchasingEnabled: true,
    excludedDeliveryDates: [],
    deliverySeasonStart: new Date(),
    deliverySeasonEnd: new Date()
  });
  private _deliveryRules = new BehaviorSubject<DeliveryRule[]>([]);

  // Resolves once the store_settings remote-config read has settled (success OR
  // failure). Guards await this before reading shopSettings$ so they see the real
  // flag values instead of the conservative seed above. Defaults to a resolved
  // promise so it's always awaitable — the SSR path early-returns before the
  // browser branch reassigns it to the real fetch.
  settingsReady: Promise<void> = Promise.resolve();

  private _cart$ = this._cart.asObservable().pipe(shareReplay(1));
  private _settings$ = this._shopSettings.asObservable().pipe(shareReplay(1));
  private _deliveryRules$ = this._deliveryRules.asObservable().pipe(shareReplay(1));

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private dialogService: NbDialogService,
    private toastService: NbToastrService,
    private analyticsService: AnalyticsService,
    private helpService: HelpService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    // Skip Firestore on SSR/prerender — App Check has no token on the server
    // (see app.module.ts provideAppCheck), so once enforcement is enabled these
    // reads would be rejected and break prerender. The prerendered HTML doesn't
    // expose this data anyway; the browser refetches on hydration.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.settingsReady = this.helpService
      .getRemoteConfigData('store_settings', true)
      .then((res) => {
        if (res) {
          if (!environment.production) {
            this._shopSettings.next({
              isShopEnabled: true,
              isExclusivesEnabled: true,
              isGiftCardPurchasingEnabled: true,
              excludedDeliveryDates: [],
              deliverySeasonStart: new Date(),
              deliverySeasonEnd: new Date()
            });
          } else {
            this._shopSettings.next(JSON.parse(res));
          }
        }
      })
      .catch((err) => {
        // This is a public read (remote-configs rule: allow read: if true), but
        // App Check enforcement can reject it with permission-denied while the
        // reCAPTCHA token is still being minted on first paint. Keep the
        // conservative seeded _shopSettings and swallow — without this .catch the
        // rejection leaks via Zone into the global ErrorHandler -> New Relic.
        console.error('store_settings remote-config read failed; using seeded shop settings', err);
      });

    this._db
      .queryCollection$<DeliveryRule>('delivery-rules', [limit(10)])
      .pipe(
        // Same App Check timing race as above. Retry a couple times to let the
        // token settle before giving up; the read is idempotent.
        retry({ count: 2, delay: 3000 })
      )
      .subscribe({
        next: (rules) => this._deliveryRules.next(rules),
        error: (err) => {
          // Keep the seeded [] — losing delivery rules degrades gracefully and
          // must not leak to the global ErrorHandler/New Relic.
          console.error('delivery-rules subscription failed; keeping empty rules', err);
        }
      });
  }

  async initCartSubscription() {
    if (this._cartSubInit) {
      return;
    }
    try {
      this._cartSubInit = true;
      const user = await this.authService.$user.pipe(take(1)).toPromise();
      let tmpCart = await this.activeCart$().pipe(take(1)).toPromise();
      if (user) {
        // Ensure the cart doc exists before opening the listener — otherwise
        // docSnapshots emits an empty snapshot and pushes undefined into the
        // cart BehaviorSubject, which downstream consumers don't tolerate.
        const existing = await this._db.readDoc<ShoppingCart>('shopping-carts', user.uid);
        if (!existing) {
          this._cartSubInit = false;
          return this.initializeNewCart();
        }

        this.authService.$user
          .pipe(
            takeUntil(this.authService.logout$),
            switchMap((user) => {
              if (user && user.uid) {
                return this._db.queryDoc$<ShoppingCart>('shopping-carts', user.uid).pipe(
                  // Doc existence/ownership is verified above, so a denial here is
                  // a transient App Check / token race — retry with a fresh token.
                  // No catchError: a failure surviving retries should reach NR.
                  retry({ count: 2, delay: 3000 })
                );
              }
              return this.activeCart$();
            }),
            takeUntil(this.authService.logout$),
            shareReplay(1)
          )
          .subscribe(
            async (cart: ShoppingCart) => {
              if (cart !== this._cart.value) {
                this._cart.next(cart);
              }
              if (tmpCart && tmpCart.items.length > 0 && !tmpCart.id) {
                // add temp to actual cart
                const items = tmpCart.items;
                tmpCart = null;
                await this.addToCart(items, true);
              }
            },
            async (error) => {
              // likely missing a cart. initalize a new one..
              console.log('Missing Cart');
              this._cartSubInit = false;
              await this.initializeNewCart();
            },
            () => {
              console.log('COMPLETE');
              this._cartSubInit = false;
              this.resetCart();
            }
          );
      } else {
        this._cartSubInit = false;
      }
    } catch (error) {
      this._cartSubInit = false;
      console.error('err gettin user', error);
    }
  }

  async initializeNewCart() {
    const user = await this.authService.$user.pipe(take(1)).toPromise();
    // create cart on database
    const newCart: ShoppingCart = initShoppingCart({
      id: user.uid,
      isGuestCart: user.isAnonymous,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: user.uid,
      updatedBy: user.uid
    });
    await this._db.setDoc('shopping-carts', user.uid, newCart);
    await this.initCartSubscription();
  }

  activeCart$() {
    return this._cart$;
  }

  shopSettings$() {
    return this._settings$;
  }

  deliveryRules$() {
    return this._deliveryRules$;
  }

  resetCart() {
    this._cart.next({ items: [], deliveryAddress: {} } as ShoppingCart);
  }

  async addToCart(newCartItems: CartItem[], forceQuantities = false): Promise<boolean> {
    // check if logged in.
    const user = await this.authService.$user.pipe(take(1)).toPromise();
    const currentCart = await this.activeCart$().pipe(take(1)).toPromise();
    await this.analyticsService.logEvent('add_to_cart');
    if (user && !this._cartSubInit) {
      // user logged in but cart service hasn't been initialized
      this._cart.next({ ...currentCart, items: [...currentCart.items, ...newCartItems] });
      await this.initCartSubscription();
      return true;
    } else {
      if (!user || !currentCart.id || currentCart.id === '') {
        // cache in temp cart
        this._cart.next({ ...currentCart, items: [...currentCart.items, ...newCartItems] });
        // user needs to log in as user or guest
        this.dialogService.open(GuestShopLoginComponent, {});
        return false;
      } else {
        // popup on completion
        const endpoint = forceQuantities
          ? `${this._API_URL}/cart/${currentCart.id}/qtys`
          : `${this._API_URL}/cart/${currentCart.id}/items`;
        await this.http
          .put(endpoint, {
            items: newCartItems
          })
          .pipe(take(1))
          .toPromise();
        return true;
      }
    }
  }

  async removeFromCart(cartItemId: string) {
    await this.analyticsService.logEvent('remove_from_cart', { currency: 'USD' });
    const currentCart = await this.activeCart$().pipe(take(1)).toPromise();
    const endpoint = `${this._API_URL}/cart/${currentCart.id}/items/${cartItemId}`;
    if (currentCart.id && currentCart.id !== '') {
      await this.http.delete(endpoint).pipe(take(1)).toPromise();
    }
    return;
  }

  async updateCartDeliverySlots(address?: DeliveryAddress) {
    const currentCart = await this.activeCart$().pipe(take(1)).toPromise();
    if ((!currentCart.deliveryAddress || !currentCart.deliveryAddress.zip) && !address) {
      return;
    }
    const endpoint = `${this._API_URL}/delivery/slots/${currentCart.id}`;
    if (currentCart.id && currentCart.id !== '') {
      await this.http
        .put(endpoint, {
          address: address ? address : currentCart.deliveryAddress
        })
        .pipe(take(1))
        .toPromise();
    }
    return;
  }

  async getAvailableDeliverySlots(): Promise<DeliverySlotOpening[]> {
    const endpoint = `${this._API_URL}/delivery/slots/availability`;
    return this.http
      .get(endpoint)
      .pipe(
        take(1),
        map((res: any) => {
          return res.slots;
        })
      )
      .toPromise();
  }

  async holdDeliveryDateSlots(requestedSlots: DeliverySlot[]) {
    const endpoint = `${this._API_URL}/delivery/date/hold`;
    return this.http.put(endpoint, { requestedSlots }).pipe(take(1)).toPromise();
  }

  async releaseDeliveryDateSlots(releasedSlots: DeliverySlot[]) {
    const endpoint = `${this._API_URL}/delivery/date/release`;
    return this.http.put(endpoint, { releasedSlots }).pipe(take(1)).toPromise();
  }

  async createCheckoutSession(): Promise<CheckoutSession> {
    const endpoint = `${this._API_URL}/checkout`;
    return this.http.post<CheckoutSession>(endpoint, {}).pipe(take(1)).toPromise();
  }

  async checkOrderStatus(searchTerm: string): Promise<any> {
    const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/;
    const params = {};
    searchTerm = searchTerm.toLowerCase();
    if (searchTerm && emailRegex.test(searchTerm)) {
      params['email'] = searchTerm;
    } else {
      params['orderid'] = searchTerm;
    }
    const endpoint = `${this._API_URL}/checkout-session/summaries`;
    return this.http
      .get<any>(endpoint, { params })
      .pipe(
        take(1),
        map((res) => {
          return res.data?.summaries;
        })
      )
      .toPromise();
  }

  // return rules eligible for given zip
  async getDeliveryRules(zip: string): Promise<DeliveryRule[]> {
    const endpoint = `${this._API_URL}/delivery/rules`;
    return this.http
      .get(endpoint, {
        params: {
          zip
        }
      })
      .pipe(
        take(1),
        map((res: any) => {
          return res.rules;
        })
      )
      .toPromise();
  }

  getCategoryShopItemScrollerObservable(
    categories: string[],
    rowItems = 3,
    cursors?: Map<number, any[]>
  ): Observable<GalleryRecord[][]> {
    const constraints: QueryConstraint[] = [
      where('hasLineItems', '==', true),
      where('category', 'in', categories),
      orderBy('name', 'asc')
    ];
    if (cursors && cursors.has(0)) {
      const cursor = cursors.get(0).pop().name;
      constraints.push(startAfter(cursor));
    }
    constraints.push(limit(20));

    return this._db.queryCollection$<GalleryRecord>('gallery-records', constraints).pipe(
      map((rec) => {
        let row = [];
        const records: GalleryRecord[][] = [];
        rec.forEach((r, index) => {
          if (index !== 0 && index % rowItems === 0) {
            records.push([...row]);
            row = [];
          }
          row.push(r);
        });
        if (row.length > 0) {
          records.push([...row]);
        }
        return records;
      })
    );
  }

  isValidItemQuantity(item: CartItem, deliveryRule: DeliveryRule) {
    if (
      item.quantity < item.minQty ||
      item.quantity > item.maxQty ||
      (item.retrievalMethodType === 'delivery' && item.quantity < deliveryRule?.minQty) ||
      (item.retrievalMethodType === 'delivery' && item.quantity > deliveryRule?.maxQty)
    ) {
      let message = `Cart item has a min limit of ${item.minQty} and a max limit of ${item.maxQty}.`;
      if (item.retrievalMethodType === 'delivery') {
        message += ` On delivery of this type of item, there is a min limit of ${deliveryRule?.minQty} and max limit of ${deliveryRule?.maxQty}.`;
      }
      this.toastService.danger(message, 'Issue updating cart item.', {
        duration: 15000
      });
      return false;
    }
    return true;
  }

  getDesignConsultationDepositPaymentLink() {
    return (
      environment.stripe.designDepositPaymentLink +
      `design-consultations${CLIENT_REFERENCE_ID_SEPARATOR}`
    );
  }
}
